import React, { useEffect, useState } from "react";
import SidebarOrganizer from "../../components/Sidebar/SidebarOrganizer";
import { db, auth } from "../../firebase";
import {
  collection,
  onSnapshot,
  getDocs,
  updateDoc,
  doc,
  query,
  where,
} from "firebase/firestore";
import "./OrganizerDashboard.css";

export default function Progress() {
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [members, setMembers] = useState([]);

  // Load Groups - filtered by organizerId
  useEffect(() => {
    if (!auth.currentUser) return;

    const currentUserId = auth.currentUser.uid;
    const groupsQuery = query(collection(db, "groups"), where("organizerId", "==", currentUserId));
    const unsubGroups = onSnapshot(groupsQuery, (snap) => {
      const data = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setGroups(data);
      if (data.length > 0 && !selectedGroup) {
        setSelectedGroup(data[0]);
      }
    });
    return () => unsubGroups();
  }, []);

  // Load Members
  useEffect(() => {
    if (!selectedGroup) return;
    const unsubMembers = onSnapshot(
      collection(db, `groups/${selectedGroup.id}/members`),
      (snap) => {
        const membersData = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setMembers(membersData);
      }
    );
    return () => unsubMembers();
  }, [selectedGroup]);

  // Toggle paid/unpaid
  const togglePaidStatus = async (memberId, currentStatus) => {
    const memberRef = doc(db, `groups/${selectedGroup.id}/members/${memberId}`);
    await updateDoc(memberRef, { paid: !currentStatus });
  };

  // Count paid
  const paidCount = members.filter((m) => m.paid).length;

  return (
    <div className="dashboard-layout">
      <SidebarOrganizer />
      <div className="organizer-dashboard">
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700', 
            color: '#111827', 
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}>
            📊 Group Progress
          </h1>
          <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
            Track member payments and group performance
          </p>
        </div>

        {/* Group Selection */}
        <div style={{
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          padding: '20px 24px',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: '1px solid #e5e7eb',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
        }}>
          <label htmlFor="group-select" style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#111827',
            margin: 0,
          }}>
            Select Group:
          </label>
          <select
            id="group-select"
            value={selectedGroup?.id || ""}
            onChange={(e) => {
              const group = groups.find((g) => g.id === e.target.value);
              setSelectedGroup(group);
            }}
            style={{
              flex: 1,
              maxWidth: '400px',
              padding: "12px 16px",
              borderRadius: '10px',
              border: "2px solid #e5e7eb",
              fontSize: '15px',
              backgroundColor: '#fff',
              cursor: 'pointer',
              transition: 'all 0.2s',
              outline: 'none',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#2563eb';
              e.target.style.boxShadow = '0 0 0 3px rgba(37, 99, 235, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          >
            {groups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.groupName}
              </option>
            ))}
          </select>
        </div>

        {/* Group Summary */}
        {selectedGroup && (
          <div
            className="progress-summary"
            style={{
              background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
              borderRadius: '16px',
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
              padding: '28px',
              marginBottom: '24px',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ 
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '20px',
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: '700',
                fontSize: '18px',
              }}>
                📊
              </div>
              <h2 style={{ 
                fontWeight: '700', 
                fontSize: '22px', 
                margin: 0,
                color: '#111827',
              }}>
                {selectedGroup.groupName} Summary
              </h2>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginBottom: '16px',
            }}>
              <div style={{
                padding: '16px',
                backgroundColor: '#eff6ff',
                borderRadius: '12px',
                border: '1px solid #bfdbfe',
              }}>
                <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600', marginBottom: '6px' }}>
                  💰 Contribution
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                  ₹{selectedGroup.contribution}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#eff6ff',
                borderRadius: '12px',
                border: '1px solid #bfdbfe',
              }}>
                <div style={{ fontSize: '13px', color: '#1e40af', fontWeight: '600', marginBottom: '6px' }}>
                  👥 Members
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#1e40af' }}>
                  {selectedGroup.currentMembers} / {selectedGroup.maxMembers}
                </div>
              </div>
              <div style={{
                padding: '16px',
                backgroundColor: '#fef3c7',
                borderRadius: '12px',
                border: '1px solid #fde68a',
              }}>
                <div style={{ fontSize: '13px', color: '#92400e', fontWeight: '600', marginBottom: '6px' }}>
                  ✅ Paid
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', color: '#92400e' }}>
                  {paidCount} / {members.length}
                </div>
              </div>
            </div>
            <div style={{
              padding: '12px',
              backgroundColor: '#f9fafb',
              borderRadius: '8px',
              fontSize: '13px',
              color: '#6b7280',
              border: '1px solid #e5e7eb',
            }}>
              <strong>Note:</strong> Winners pay ₹6000 from the month after they win. Others pay ₹5000.
            </div>
          </div>
        )}

        {/* Members Table */}
        <div
          className="members-box"
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)",
            borderRadius: '16px',
            boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            padding: '28px',
            border: '1px solid #e5e7eb',
          }}
        >
          <div style={{ 
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            marginBottom: '20px',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
              fontWeight: '700',
              fontSize: '18px',
            }}>
              👥
            </div>
            <h2 style={{ 
              fontWeight: '700', 
              fontSize: '22px', 
              margin: 0,
              color: '#111827',
            }}>
              Members List
            </h2>
          </div>
          {members.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '40px 20px',
              color: '#6b7280',
            }}>
              <div style={{ 
                width: '56px',
                height: '56px',
                margin: '0 auto 16px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #e5e7eb 0%, #d1d5db 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '28px',
              }}>👤</div>
              <p style={{ margin: 0, fontSize: '16px', fontWeight: '500' }}>No members found in this group.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table
                style={{
                  width: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: "15px",
                }}
              >
                <thead>
                  <tr>
                    <th style={{ 
                      textAlign: "left", 
                      padding: "16px", 
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      color: '#111827',
                      fontWeight: '700',
                      fontSize: '14px',
                      borderTopLeftRadius: '12px',
                    }}>Member</th>
                    <th style={{ 
                      textAlign: "left", 
                      padding: "16px", 
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      color: '#111827',
                      fontWeight: '700',
                      fontSize: '14px',
                    }}>Status</th>
                    <th style={{ 
                      textAlign: "left", 
                      padding: "16px", 
                      background: 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                      color: '#111827',
                      fontWeight: '700',
                      fontSize: '14px',
                      borderTopRightRadius: '12px',
                    }}>Toggle</th>
                  </tr>
                </thead>
                <tbody>
                  {members.map((m, index) => (
                    <tr 
                      key={m.id} 
                      style={{ 
                        borderBottom: "1px solid #e5e7eb",
                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb',
                        transition: 'background-color 0.2s',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = index % 2 === 0 ? '#ffffff' : '#f9fafb';
                      }}
                    >
                      <td style={{ padding: "16px", fontWeight: '600', color: '#111827' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <div style={{
                            width: '36px',
                            height: '36px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            fontWeight: '700',
                            fontSize: '14px',
                          }}>
                            {m.name ? m.name.charAt(0).toUpperCase() : '?'}
                          </div>
                          {m.name}
                        </div>
                      </td>
                      <td style={{ padding: "16px" }}>
                        {m.paid ? (
                          <span
                            style={{
                              background: "linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)",
                              color: "#1e40af",
                              padding: "6px 14px",
                              borderRadius: "20px",
                              fontWeight: 700,
                              fontSize: '13px',
                              border: '1px solid #93c5fd',
                            }}
                          >
                            ✔ Paid
                          </span>
                        ) : (
                          <span
                            style={{
                              background: "linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)",
                              color: "#dc2626",
                              padding: "6px 14px",
                              borderRadius: "20px",
                              fontWeight: 700,
                              fontSize: '13px',
                              border: '1px solid #fca5a5',
                            }}
                          >
                            ✘ Not Paid
                          </span>
                        )}
                      </td>

                      {/* Toggle Switch */}
                      <td style={{ padding: "16px" }}>
                        <label className="switch">
                          <input
                            type="checkbox"
                            checked={m.paid}
                            onChange={() => togglePaidStatus(m.id, m.paid)}
                          />
                          <span className="slider round"></span>
                        </label>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Toggle Switch CSS */}
      <style>
        {`
          .switch {
            position: relative;
            display: inline-block;
            width: 46px;
            height: 24px;
          }
          .switch input {
            opacity: 0;
            width: 0;
            height: 0;
          }
          .slider {
            position: absolute;
            cursor: pointer;
            top: 0; left: 0; right: 0; bottom: 0;
            background-color: #ccc;
            transition: 0.4s;
            border-radius: 24px;
          }
          .slider:before {
            position: absolute;
            content: "";
            height: 18px; width: 18px;
            left: 3px; bottom: 3px;
            background-color: white;
            transition: 0.4s;
            border-radius: 50%;
          }
          input:checked + .slider {
            background-color: #2563eb;
          }
          input:checked + .slider:before {
            transform: translateX(22px);
          }
        `}
      </style>
    </div>
  );
}
